/* Weekly reset countdown
   Counts down to the next occurrence of a given weekday + hour in a fixed
   timezone, then resets automatically once that moment passes. Because the
   target is resolved in an explicit IANA timezone (not the browser's), every
   visitor counts down to the same instant regardless of their own locale, and
   DST shifts are handled automatically.

   Markup:
     <count-down data-day="4" data-hour="16" data-tz="America/Denver">
       <span data-cd-days></span>
       <span data-cd-hrs></span>
       <span data-cd-mins></span>
       <span data-cd-secs></span>
     </count-down>
   data-day: 0 = Sunday ... 6 = Saturday (matches Date.getDay()).
   data-tz : IANA timezone name; falls back to the visitor's local time. */

class CountDown extends HTMLElement {
  connectedCallback() {
    this.day = parseInt(this.dataset.day, 10);
    this.hour = parseInt(this.dataset.hour, 10) || 0;
    this.tz = this.dataset.tz || undefined;
    if (Number.isNaN(this.day)) return;

    this.parts = {
      days: this.querySelector("[data-cd-days]"),
      hrs: this.querySelector("[data-cd-hrs]"),
      mins: this.querySelector("[data-cd-mins]"),
      secs: this.querySelector("[data-cd-secs]"),
    };

    this.target = this.nextTarget();
    this.render();
    this.timer = setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // Wall-clock components of `date` as seen in this.tz.
  wallParts(date) {
    const names = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: this.tz,
      hourCycle: "h23",
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const p = {};
    for (const part of fmt.formatToParts(date)) p[part.type] = part.value;
    return {
      year: +p.year,
      month: +p.month,
      day: +p.day,
      hour: +p.hour,
      minute: +p.minute,
      second: +p.second,
      weekday: names[p.weekday],
    };
  }

  // UTC offset (ms) of this.tz at `date`: wall-clock-as-UTC minus the real instant.
  tzOffset(date) {
    const p = this.wallParts(date);
    const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUTC - date.getTime();
  }

  // Convert a wall-clock time (given as a UTC-based timestamp) in this.tz to the
  // real instant. Resolved twice so a DST change between guess and result settles.
  wallToInstant(wallUTC) {
    let instant = wallUTC - this.tzOffset(new Date(wallUTC));
    instant = wallUTC - this.tzOffset(new Date(instant));
    return instant;
  }

  // Next occurrence of the configured weekday + hour in this.tz, in the future.
  nextTarget() {
    const now = Date.now();
    const p = this.wallParts(new Date(now));
    const delta = (this.day - p.weekday + 7) % 7;
    let wall = Date.UTC(p.year, p.month - 1, p.day + delta, this.hour, 0, 0);
    let instant = this.wallToInstant(wall);
    if (instant <= now) {
      wall = Date.UTC(p.year, p.month - 1, p.day + delta + 7, this.hour, 0, 0);
      instant = this.wallToInstant(wall);
    }
    return new Date(instant);
  }

  render() {
    const now = Date.now();
    let ms = this.target.getTime() - now;
    if (ms <= 0) {
      this.target = this.nextTarget();
      ms = this.target.getTime() - now;
    }

    const total = Math.floor(ms / 1000);
    const days = Math.floor(total / 86400);
    const hrs = Math.floor((total % 86400) / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;

    this.set(this.parts.days, days, "day");
    this.set(this.parts.hrs, hrs, "hr");
    this.set(this.parts.mins, mins, "min");
    this.set(this.parts.secs, secs, "sec");
  }

  set(el, value, unit) {
    if (!el) return;
    el.textContent = value + " " + unit + (value === 1 ? "" : "s");
  }
}

customElements.define("count-down", CountDown);
